/**
 * Terpenes Page - Modal Trigger
 * Part of Story 1.4: Build Terpene Browser Modal
 * 
 * Simple page that auto-opens the terpene browser modal.
 * The modal contains all the browser functionality.
 */

'use client'

import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Leaf, FlaskConical, Sparkles } from 'lucide-react'
import TerpeneBrowserModal from '@/components/terpenes/TerpeneBrowserModal'

export default function TerpenesPage() {
  const [open, setOpen] = useState(false)

  // Auto-open modal on mount
  useEffect(() => {
    setOpen(true)
  }, [])

  return (
    <>
      {/* Modal */}
      <TerpeneBrowserModal open={open} onOpenChange={setOpen} />

      {/* Landing Page Content (shown when modal is closed) */}
      <div className="min-h-screen bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-4 py-20">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            {/* Hero Section */}
            <div className="space-y-4">
              <div className="flex justify-center">
                <div className="p-4 bg-emerald-500/10 rounded-full">
                  <Leaf className="h-12 w-12 text-emerald-400" />
                </div>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold text-white">
                Cannabis Terpene Browser
              </h1>
              
              <p className="text-xl text-white/70 max-w-2xl mx-auto">
                Explore the fascinating world of terpenes - the aromatic compounds that give cannabis its unique flavors, aromas, and effects.
              </p>
            </div>

            {/* CTA Button */}
            <Button
              onClick={() => setOpen(true)}
              size="lg"
              className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-8 py-6 text-lg"
            >
              <FlaskConical className="mr-2 h-5 w-5" />
              Browse Terpenes
            </Button>

            {/* Feature Cards */}
            <div className="grid md:grid-cols-3 gap-6 mt-12">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-6 space-y-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto">
                  <Sparkles className="h-5 w-5 text-blue-400" />
                </div>
                <h3 className="text-lg font-semibold text-white">250+ Terpenes</h3>
                <p className="text-sm text-white/60">
                  Comprehensive database of cannabis terpenes with detailed information.
                </p>
              </div>

              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-6 space-y-3">
                <div className="w-10 h-10 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto">
                  <FlaskConical className="h-5 w-5 text-purple-400" />
                </div>
                <h3 className="text-lg font-semibold text-white">Research-Backed</h3>
                <p className="text-sm text-white/60">
                  Scientific data on effects, benefits, and natural sources.
                </p>
              </div>

              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-6 space-y-3">
                <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
                  <Leaf className="h-5 w-5 text-green-400" />
                </div>
                <h3 className="text-lg font-semibold text-white">Interactive Browser</h3>
                <p className="text-sm text-white/60">
                  Filter by effects, sources, and categories to find specific terpenes.
                </p>
              </div>
            </div>

            {/* Educational Note */}
            <div className="mt-16 p-6 bg-gradient-to-r from-emerald-500/10 to-green-500/10 rounded-lg border border-emerald-500/20">
              <p className="text-white/80 leading-relaxed">
                <strong className="text-emerald-400">Did you know?</strong> Terpenes are responsible for the distinctive aromas of cannabis strains and work synergistically with cannabinoids to produce the "entourage effect" - enhancing and modulating the overall experience.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}